import { Injectable } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { IntlService } from '@frontend/vanilla/core';

@Injectable({ providedIn: 'root' })
export class DateFormatterService {
    private readonly MINUTE = 60 * 1000;
    private readonly HOUR = 60 * this.MINUTE;
    private readonly DAY = 24 * this.HOUR;

    private _formatMap?: { [key: string]: { title: string; format: string[] } };

    /**
     * Initializes the DateFormatterService.
     *
     * @param  sitecore - Provides global messages and localization settings.
     * @param  intlService - Provides internationalization utilities for date and time formatting.
     */
    constructor(
        private sitecore: Sitecore,
        private intlService: IntlService,
    ) {}

    get formatMap() {
        if (!this._formatMap) {
            this._formatMap = {
                today: { title: this.sitecore.globalMessages.Today, format: ['shortTime'] },
                tomorrow: { title: this.sitecore.globalMessages.Tomorrow, format: ['shortTime'] },
                other: { title: this.sitecore.globalMessages.TimerOther, format: ['shortDate'] },
            };
        }

        return this._formatMap;
    }

    /**
     * Formats a given date into a string based on its category ("Today", "Tomorrow", or other).
     *
     * @param  startDate - The date to format.
     * @returns  - A formatted string combining the category title and formatted date.
     */
    formatDate(startDate: Date): string {
        const formatterKey = this.getFormatKey(startDate);
        const formatter = this.formatMap[formatterKey];

        return formatter.title.replace('{date}', formatter.format.map((f) => this.intlService.formatDate(startDate, f)).join(' ') || '');
    }

    /**
     * Formats a given date into a string that includes both the date and time.
     *
     * @param  startDate - The date to format.
     * @returns  - A formatted string combining the category title, formatted date, and time.
     */
    formatDateWithTime(startDate: Date): string {
        return `${this.formatDate(startDate)} ${this.intlService.formatDate(startDate, 'shortTime')}`;
    }

    /**
     * Determines the formatting key for a given date based on its relation to the current date.
     *
     * @private
     * @param  eventDate - The date to evaluate.
     * @returns  - The key ("today", "tomorrow", or "other") corresponding to the date's category.
     */
    private getFormatKey(eventDate: Date): string {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        const tomorrow = new Date(today.getTime() + this.DAY);

        if (eventDate < today) {
            return 'today';
        }
        if (eventDate < tomorrow) {
            return 'tomorrow';
        }

        return 'other';
    }
}
